import React from 'react';
import { Rocket, Users, MapPin, TrendingUp, Award, Megaphone, Code, Building } from 'lucide-react';

const milestones = [
  {
    year: 'The Beginning',
    title: 'One Man, One Vision',
    description: 'Started as a solo operation in Philadelphia with just pen and paper, delivering quality appliance repairs one home at a time.',
    icon: Rocket,
  },
  {
    year: 'Development',
    title: 'Systems & Growth',
    description: 'Built integrated systems and workflows from scratch, transforming manual processes into scalable operations.',
    icon: TrendingUp,
  },
  {
    year: 'Expansion',
    title: 'Management & Marketing',
    description: 'Evolved into a full management and marketing company, training technicians and developing growth strategies for 15+ locations.',
    icon: Users,
  },
  {
    year: 'Today',
    title: 'Going Nationwide',
    description: 'Operating international call center, continuous development, and expanding to bring BBM quality service to all 50 states.',
    icon: MapPin,
  },
];

const AboutSection = () => {
  return (
    <section data-testid="about-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
            About Us
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            From Engineer to Industry Leader
          </h2>
          <p className="text-lg text-muted-foreground">
            BBM Appliances is a management, marketing, and development company founded by an engineer 
            with decades of experience across multiple technical industries, driven by a passion for excellence and innovation.
          </p>
        </div>

        {/* Founder Story */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-semibold">
              <Award className="w-4 h-4" />
              Our Story
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
              Built on Experience, Driven by Innovation
            </h3>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Our founder, an experienced engineer with a background spanning multiple technical industries, 
                saw an opportunity to bring professional standards to the appliance repair industry. 
                Starting the first BBM LLC in Philadelphia, he personally handled every repair — 
                scheduling appointments with pen and paper, diagnosing issues, and ensuring every customer was satisfied.
              </p>
              <p>
                As demand grew, so did the vision. Custom systems were developed to streamline operations, 
                replacing manual processes with integrated workflows. This foundation allowed BBM to scale 
                while maintaining the quality that built our reputation.
              </p>
              <p>
                Today, BBM Appliances has evolved into a full-service <span className="font-medium text-foreground">management, marketing, and development company</span> overseeing 
                15+ appliance repair locations. We handle everything — from technician training and workflow optimization 
                to marketing strategies and business development — so our partner locations can focus on what they do best: fixing appliances.
              </p>
              <p>
                Our dedicated international call center provides support 7 days a week, 
                and our in-house development team continuously improves our systems to deliver better service.
              </p>
              <p className="font-medium text-foreground">
                And we're just getting started — with plans to expand nationwide, bringing reliable appliance repair to homes across America.
              </p>
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl p-8">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
                  <div className="text-4xl font-bold text-primary mb-2">15+</div>
                  <p className="text-sm text-muted-foreground">Service Locations</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
                  <div className="text-4xl font-bold text-primary mb-2">10K+</div>
                  <p className="text-sm text-muted-foreground">Happy Customers</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
                  <div className="text-4xl font-bold text-primary mb-2">7/7</div>
                  <p className="text-sm text-muted-foreground">Days Available</p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm text-center">
                  <div className="text-4xl font-bold text-accent mb-2">50</div>
                  <p className="text-sm text-muted-foreground">States Goal</p>
                </div>
              </div>
            </div>
            {/* Decorative */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-3xl"></div>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          <h3 className="font-heading text-2xl font-bold text-foreground text-center mb-12">
            Our Journey
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone, index) => (
              <div 
                key={index}
                data-testid={`milestone-${index}`}
                className="relative bg-secondary/50 rounded-2xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <milestone.icon className="w-6 h-6 text-primary" />
                  </div>
                  <span className="text-sm font-semibold text-primary">{milestone.year}</span>
                </div>
                <h4 className="font-heading font-semibold text-lg text-foreground mb-2">
                  {milestone.title}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {milestone.description}
                </p>
                {index < milestones.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-border"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Values */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-primary rounded-2xl p-8 text-white">
            <Building className="w-10 h-10 mb-4 opacity-80" />
            <h4 className="font-heading font-bold text-xl mb-2">Management</h4>
            <p className="text-white/80 text-sm leading-relaxed">
              We manage 15+ appliance repair locations, handling operations, training, and quality control so technicians can focus on repairs.
            </p>
          </div>
          <div className="bg-accent rounded-2xl p-8 text-white">
            <Megaphone className="w-10 h-10 mb-4 opacity-80" />
            <h4 className="font-heading font-bold text-xl mb-2">Marketing</h4>
            <p className="text-white/80 text-sm leading-relaxed">
              Our marketing team drives growth through strategic campaigns, online presence, and customer acquisition for all our locations.
            </p>
          </div>
          <div className="bg-foreground rounded-2xl p-8 text-white">
            <Code className="w-10 h-10 mb-4 opacity-80" />
            <h4 className="font-heading font-bold text-xl mb-2">Development</h4>
            <p className="text-white/80 text-sm leading-relaxed">
              Our in-house development team builds and maintains custom systems, workflows, and technology to optimize every aspect of service.
            </p>
          </div>
        </div>

        {/* Commitment */}
        <div className="mt-12 bg-secondary/50 rounded-3xl p-8 md:p-12">
          <div className="text-center max-w-3xl mx-auto">
            <Award className="w-12 h-12 mx-auto mb-6 text-primary" />
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
              Our Commitment
            </h3>
            <p className="text-muted-foreground text-lg leading-relaxed">
              We only hire experienced, professional technicians who work exclusively for BBM. 
              Every team member is trained in our proven workflows and committed to our core values: 
              showing up on time, guaranteeing our work, and providing top-notch service to every customer.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
